import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AiAgentSession } from '@lgg/isomorphic/types/__generated__/graphql';
import { useVisible } from 'src/hooks/use-visible';
import { ContactInteractionExtraDetails } from './interaction-details';
import { InteractionDetailsDrawer } from './interaction-details-drawer';
import {
  ActionsDetailSection,
  AiAgentsActionsBadge,
  AiAgentsGoalsBadge,
  getInteractionDetailBrief,
  GoalsDetailSection,
  InteractionTruncatedTextDetail,
  MoreDetailsOption,
  SectionDivider,
  SingleSessionView,
  SummaryBadge,
  SummaryDetailSection,
  TakenOverByBadge,
  TakenOverByDetailSection,
} from './shared';

type AiAgentSessionDetailsProps = {
  aiAgentSession: AiAgentSession;
};

export const AiAgentSessionDetailSections = ({
  aiAgentSession,
  singleSessionView,
}: AiAgentSessionDetailsProps & { singleSessionView?: SingleSessionView }) => {
  const { t } = useTranslation(['conversations']);
  const { summary, goals, toolCalls, aiAgent, takenOverBy } = aiAgentSession;

  return (
    <>
      {summary && (
        <>
          <SectionDivider />
          <SummaryDetailSection
            title={
              singleSessionView?.summaryTitle ??
              t(
                'conversations:contactInteractionBubble.aiAgentSessionDetails.summary.title',
              )
            }
            content={summary}
            testId="ai-agent-session-summary-section"
            compact={!!singleSessionView}
          />
        </>
      )}
      {goals.length > 0 && (
        <GoalsDetailSection
          records={[{ agent: aiAgent, goals }]}
          singleSessionView={singleSessionView}
        />
      )}
      {toolCalls.length > 0 && (
        <ActionsDetailSection
          records={[{ agent: aiAgent, actions: toolCalls }]}
          singleSessionView={singleSessionView}
        />
      )}
      <TakenOverByDetailSection
        takenOverBy={takenOverBy ?? undefined}
        compact={!!singleSessionView}
      />
    </>
  );
};

export const AiAgentSessionDetails = ({ aiAgentSession }: AiAgentSessionDetailsProps) => {
  const { t } = useTranslation(['conversations', 'common']);
  const detailsDrawerVisibilityHandler = useVisible();

  const summaryBadge = useMemo(() => {
    if (!aiAgentSession.summary) return null;

    return <SummaryBadge onClick={detailsDrawerVisibilityHandler.show} />;
  }, [detailsDrawerVisibilityHandler.show, aiAgentSession.summary]);

  const goalsBadge = useMemo(() => {
    const goals = aiAgentSession.goals;
    if (!goals.length) return null;

    return (
      <AiAgentsGoalsBadge goals={goals} onClick={detailsDrawerVisibilityHandler.show} />
    );
  }, [aiAgentSession.goals, detailsDrawerVisibilityHandler.show]);

  const actionsBadge = useMemo(() => {
    const actionsCount = aiAgentSession.toolCalls.length;

    return (
      <AiAgentsActionsBadge
        actionsCount={actionsCount}
        onClick={detailsDrawerVisibilityHandler.show}
      />
    );
  }, [aiAgentSession.toolCalls, detailsDrawerVisibilityHandler.show]);

  const briefContent = useMemo(() => {
    if (!aiAgentSession.summary) return null;

    return (
      <InteractionTruncatedTextDetail
        data-lgg-id="ai-agent-session-brief"
        direction="OUTBOUND"
      >
        {getInteractionDetailBrief(aiAgentSession.summary)}
      </InteractionTruncatedTextDetail>
    );
  }, [aiAgentSession.summary]);

  return (
    <ContactInteractionExtraDetails
      title={t('conversations:contactInteractionBubble.aiAgentSessionDetails.title')}
      testId="ai-agent-session-details"
      topContent={briefContent}
      mainContent={
        <>
          {goalsBadge}
          {actionsBadge}
          {summaryBadge}
          <TakenOverByBadge
            agentName={aiAgentSession.takenOverBy?.fullName ?? null}
            onClick={detailsDrawerVisibilityHandler.show}
          />
          <InteractionDetailsDrawer
            testId="ai-agent-session-details-drawer"
            visibilityHandler={detailsDrawerVisibilityHandler}
            title={t(
              'conversations:contactInteractionBubble.aiAgentSessionDetails.title',
            )}
            content={<AiAgentSessionDetailSections aiAgentSession={aiAgentSession} />}
          />
        </>
      }
      bottomContent={
        <MoreDetailsOption
          onClick={detailsDrawerVisibilityHandler.show}
          data-lgg-id="ai-agent-session-details-link-button"
        >
          {t('common:seeMoreDetails')}
        </MoreDetailsOption>
      }
    />
  );
};
